import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, CloudUpload, DeleteOutline, Download, EditOutlined, Inventory2, VisibilityOutlined, WarningAmber } from '@mui/icons-material';
import api from '../../utils/api';
import { getCategoryHeirarchy } from '../../redux/getCategoryHeirarchy';
import { getAttributesAddProdThunk, setBulkUploadData } from '../../redux/productSlice';
import { RowEditor } from './QuickList';
import {
  buildBulkProducts,
  buildCategoryOptions,
  buildOwnDescription,
  downloadQuickListExcel,
  finalPriceWithGst,
  getRowIssues,
  parsePriceBreaks,
} from '../../utils/quickListHelpers';
import { readLocalQuickProducts, removeLocalQuickProduct, saveLocalQuickProducts } from '../../utils/quickProductQueue';
import DeleteModal from '../../component/reusable/deleteModal/DeleteModal';

const PRIMARY = '#512DA8';
const BORDER = '#E0E2E7';
const MUTED = '#667085';
const FONT = `'Public Sans', sans-serif`;

const headSx = { fontFamily: FONT, fontWeight: 700, fontSize: '15px', color: '#1D1F2C', whiteSpace: 'nowrap', py: 1.5 };
const cellTextSx = { fontSize: '14.5px', color: '#1D1F2C', fontFamily: FONT };

const STATUS_LABELS = { PUBLISHED: 'Published', DRAFT: 'Draft', 'OUT OF STOCK': 'Out of stock' };
const STATUS_COLORS = {
  PUBLISHED: { background: '#E7F4EE', color: '#0D894F' },
  DRAFT: { background: '#F0F1F3', color: '#667085' },
  'OUT OF STOCK': { background: '#FEEDEC', color: '#F04438' },
  UPLOADED: { background: '#E8F1FD', color: '#1E5EFF' },
};

// "03 Sep 2026", as on the All Products page.
const formatAdded = (value) =>
  value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const keyFor = (item) => String(item?.row?.partNumber || item?.row?.productNumber || '').toUpperCase().replace(/[\s\-_.]/g, '');

const AllQuickProducts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categoryHeirarchy } = useSelector((state) => state.categoryHeirarchy);
  const { user } = useSelector((state) => state.user);
  const attributeList = useSelector((state) => state.products?.atributes?.attributeOptions);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [category, setCategory] = useState('ALL');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [uploadConfirm, setUploadConfirm] = useState(null); // { chosen, existing }
  const [viewItem, setViewItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(null);
  const [clearAllConfirm, setClearAllConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const categoryOptions = useMemo(() => buildCategoryOptions(categoryHeirarchy), [categoryHeirarchy]);
  const categoryById = useMemo(() => new Map(categoryOptions.map((o) => [o.id, o])), [categoryOptions]);
  const attributeOptions = useMemo(
    () => [...new Set((attributeList || []).map((attribute) => String(attribute || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [attributeList]
  );

  const load = useCallback(async () => {
    setLoading(true);
    let serverItems = [];
    try {
      const response = await api.getQuickProducts('ALL');
      serverItems = response?.data?.data?.items || [];
    } catch {
      // The browser-local queue remains available until the new backend is live.
    }

    const merged = new Map(serverItems.map((item) => [keyFor(item), item]));
    readLocalQuickProducts().forEach((item) => {
      const key = keyFor(item);
      if (key && !merged.has(key)) merged.set(key, item);
    });
    setItems([...merged.values()]);
    setLoading(false);
  }, []);

  useEffect(() => {
    dispatch(getCategoryHeirarchy());
    dispatch(getAttributesAddProdThunk());
    load();
  }, [dispatch, load]);

  const visibleItems = useMemo(
    () =>
      items.filter((item) => {
        const label = item.categoryLabel || item.row?.supplierCategory || 'Uncategorised';
        return (status === 'ALL' || item.status === status) && (category === 'ALL' || label === category);
      }),
    [items, status, category]
  );

  const categories = useMemo(
    () => [...new Set(items.map((item) => item.categoryLabel || item.row?.supplierCategory || 'Uncategorised'))].sort(),
    [items]
  );

  const toggle = (id) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectable = visibleItems.filter((item) => item.status !== 'UPLOADED');
  const allSelected = selectable.length > 0 && selectable.every((item) => selected.has(item._id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((item) => item._id)));

  const downloadSelected = () => {
    const chosen = visibleItems.filter((item) => selected.has(item._id));
    if (!chosen.length) return toast.error('Select at least one quick product');
    downloadQuickListExcel(chosen.map((item) => item.row), categoryOptions);
    return undefined;
  };

  const isLocalItem = (item) => String(item?._id || '').startsWith('local-');
  const isSuperAdmin = () => String(user?.type || user?.role || user?.user?.type || user?.user?.role || '').toUpperCase() === 'SUPERADMIN';

  const removeItemsFromQueue = async (chosen) => {
    const serverIds = chosen.filter((item) => !isLocalItem(item)).map((item) => item._id);
    if (serverIds.length) {
      try {
        await api.deleteQuickProducts(serverIds);
      } catch (error) {
        // Keep the flow usable with a server that has not received the bulk
        // delete route yet; its existing per-product delete route is enough.
        if (error?.response?.status !== 404) throw error;
        await Promise.all(serverIds.map((id) => api.deleteQuickProduct(id)));
      }
    }

    chosen.forEach((item) => removeLocalQuickProduct(item._id, item.row));
    const removedIds = new Set(chosen.map((item) => item._id));
    setItems((current) => current.filter((item) => !removedIds.has(item._id)));
    setSelected((current) => new Set([...current].filter((id) => !removedIds.has(id))));
  };

  const openEdit = (item) => setEditingItem({ ...item, row: { ...(item.row || {}) } });

  const saveEdit = async (editedRow) => {
    const nextRow = editedRow || editingItem?.row;
    const partNumber = String(nextRow?.partNumber || nextRow?.productNumber || '').trim();
    if (!partNumber) return toast.error('Part number is required');

    setActionLoading(true);
    try {
      const categoryLabel = categoryById.get(nextRow.categoryId)?.label || editingItem.categoryLabel || nextRow.supplierCategory || 'Uncategorised';
      const nextItem = { ...editingItem, categoryLabel, row: { ...nextRow, partNumber, productNumber: nextRow.productNumber || partNumber } };
      let savedItem;

      if (isLocalItem(editingItem)) {
        removeLocalQuickProduct(editingItem._id, editingItem.row);
        const savedLocalItems = saveLocalQuickProducts([nextItem]);
        savedItem = savedLocalItems.find((item) => keyFor(item) === keyFor(nextItem)) || nextItem;
      } else {
        try {
          const response = await api.updateQuickProduct(editingItem._id, { row: nextItem.row, categoryLabel });
          savedItem = response?.data?.data?.item;
          removeLocalQuickProduct('', editingItem.row);
        } catch (error) {
          // Older deployed servers do not yet expose the per-row update route.
          // Their existing save endpoint can still update all details as long as
          // the stable part number has not been changed.
          if (error?.response?.status !== 404 || keyFor(editingItem) !== keyFor(nextItem)) throw error;
          await api.saveQuickProducts([nextItem]);
          savedItem = nextItem;
        }
      }

      setItems((current) => current.map((item) => item._id === editingItem._id ? savedItem : item));
      setEditingItem(null);
      toast.success('Quick product updated successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not update quick product');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      if (!isLocalItem(deleteItem)) await api.deleteQuickProduct(deleteItem._id);
      removeLocalQuickProduct(deleteItem._id, deleteItem.row);
      setItems((current) => current.filter((item) => item._id !== deleteItem._id));
      setSelected((current) => {
        const next = new Set(current);
        next.delete(deleteItem._id);
        return next;
      });
      setDeleteItem(null);
      toast.success('Quick product deleted successfully');
    } catch (error) {
      toast.error(error?.response?.status === 404 ? 'Delete is not available on the connected server yet.' : (error?.response?.data?.message || 'Could not delete quick product'));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmBulkDelete = async () => {
    const chosen = items.filter((item) => selected.has(item._id));
    if (!chosen.length) return;
    setActionLoading(true);
    try {
      await removeItemsFromQueue(chosen);
      setBulkDeleteConfirm(null);
      toast.success(`${chosen.length} quick product(s) deleted successfully`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not delete selected quick products');
    } finally {
      setActionLoading(false);
    }
  };

  const requestClearAll = () => {
    if (!isSuperAdmin()) {
      toast.error('Only Super Admin is authorized to delete all quick products');
      return;
    }
    setClearAllConfirm(true);
  };

  const confirmClearAll = async (password) => {
    setActionLoading(true);
    try {
      await api.deleteAllQuickProducts(password);
      readLocalQuickProducts().forEach((item) => removeLocalQuickProduct(item._id, item.row));
      setItems([]);
      setSelected(new Set());
      setClearAllConfirm(false);
      toast.success('All Quick Products deleted successfully');
    } catch (error) {
      const statusCode = error?.response?.status;
      toast.error(
        statusCode === 404 || statusCode === 500
          ? 'Delete All needs the latest backend update. Please deploy the Quick Products delete API first.'
          : (error?.response?.data?.message || 'Could not delete All Quick Products')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const uploadSelected = () => {
    const chosen = visibleItems.filter((item) => selected.has(item._id));
    if (!chosen.length) return toast.error('Select at least one quick product');
    const invalid = chosen.filter((item) => getRowIssues(item.row).length);
    if (invalid.length) return toast.error(`${invalid.length} product(s) need a category or manufacturer before upload`);
    // Products already in the catalog would be overwritten: let the admin decide.
    const existing = chosen.filter((item) => item.row?.existingProductId);
    if (existing.length) {
      setUploadConfirm({ chosen, existing });
      return undefined;
    }
    startUpload(chosen);
    return undefined;
  };

  const startUpload = async (chosen) => {
    setUploadConfirm(null);
    if (!chosen.length) return;
    setActionLoading(true);
    try {
      // This is an explicit move to the next step, so transferred products
      // leave the Quick Products queue immediately and cannot be selected twice.
      await removeItemsFromQueue(chosen);
      sessionStorage.removeItem('quickProductUploadIds');
      dispatch(setBulkUploadData(buildBulkProducts(chosen.map((item) => item.row), categoryOptions)));
      navigate('/products/bulkProdUpload');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not move selected products to Bulk Upload');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#1D1F2C' }}>All Quick Products</Typography>
          <Typography sx={{ color: MUTED, fontSize: '14px', mt: 0.5 }}>
            Supplier-fetched products stay here until you choose to upload them to the main catalog.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<DeleteOutline />} onClick={requestClearAll} disabled={loading || !items.length} sx={{ textTransform: 'none', color: '#D92D20', borderColor: '#F2B8B5', '&:hover': { borderColor: '#D92D20', background: '#FFF5F4' } }}>
            Delete all
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/products/quickList')} sx={{ textTransform: 'none', background: PRIMARY }}>
            New Quick List
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: 'none', overflow: 'hidden' }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
          <Box sx={{ minWidth: 220 }}>
            <Typography sx={{ color: MUTED, fontSize: '12px', fontWeight: 600, mb: 0.5 }}>Show</Typography>
            <Select size="small" fullWidth value={status} inputProps={{ 'aria-label': 'Quick product status' }} onChange={(event) => { setStatus(event.target.value); setSelected(new Set()); }}>
              <MenuItem value="PENDING">Ready in Quick List</MenuItem>
              <MenuItem value="UPLOADED">Uploaded to catalog</MenuItem>
              <MenuItem value="ALL">All Quick Products</MenuItem>
            </Select>
          </Box>
          <Box sx={{ minWidth: 320 }}>
            <Typography sx={{ color: MUTED, fontSize: '12px', fontWeight: 600, mb: 0.5 }}>Category</Typography>
            <Select size="small" fullWidth value={category} inputProps={{ 'aria-label': 'Quick product category' }} onChange={(event) => { setCategory(event.target.value); setSelected(new Set()); }}>
              <MenuItem value="ALL">All categories</MenuItem>
              {categories.map((label) => <MenuItem key={label} value={label}>{label}</MenuItem>)}
            </Select>
          </Box>
          <Chip icon={<Inventory2 />} label={`${visibleItems.length} quick product${visibleItems.length === 1 ? '' : 's'}`} sx={{ alignSelf: 'flex-end', mb: 0.25 }} />
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress sx={{ color: PRIMARY }} /></Stack>
        ) : visibleItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Inventory2 sx={{ fontSize: 40, color: '#C9BDEB' }} />
            <Typography sx={{ mt: 1, color: MUTED }}>No quick products in this view yet.</Typography>
          </Box>
        ) : (
          // Same layout as the All Products table.
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow sx={{ background: '#F9F9FC' }}>
                  <TableCell padding="checkbox" sx={{ ...headSx, width: 48 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selected.size > 0 && !allSelected}
                      onChange={toggleAll}
                      disabled={!selectable.length}
                      sx={{ color: PRIMARY, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: PRIMARY } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...headSx, width: '23%' }}>Product</TableCell>
                  <TableCell sx={{ ...headSx, width: '16%' }}>Category</TableCell>
                  <TableCell sx={{ ...headSx, width: '8%' }}>Stock</TableCell>
                  <TableCell sx={{ ...headSx, width: '12%' }}>Unit Price</TableCell>
                  <TableCell sx={{ ...headSx, width: '7%' }}>GST</TableCell>
                  <TableCell sx={{ ...headSx, width: '13%' }}>Sale Price</TableCell>
                  <TableCell sx={{ ...headSx, width: '10%' }}>Status</TableCell>
                  <TableCell sx={{ ...headSx, width: '11%' }}>Added</TableCell>
                  <TableCell sx={{ ...headSx, width: 108, px: 0.5 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleItems.map((item) => {
                  const row = item.row || {};
                  const ourCategory = categoryById.get(row.categoryId);
                  const categoryName = ourCategory ? ourCategory.names[ourCategory.names.length - 1] : (item.categoryLabel || row.supplierCategory || '—').split(' > ').pop();
                  const firstBreak = parsePriceBreaks(row.prices, row.moq)[0];
                  const description = row.description || buildOwnDescription(row, categoryById);
                  const uploaded = item.status === 'UPLOADED';
                  return (
                    <TableRow
                      key={item._id}
                      hover
                      // Text wraps inside its own column instead of running into the next one.
                      sx={{ '& td': { borderBottom: `1px solid ${BORDER}`, fontFamily: FONT, whiteSpace: 'normal', overflow: 'hidden', verticalAlign: 'middle' } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(item._id)}
                          onChange={() => toggle(item._id)}
                          disabled={uploaded}
                          sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          {row.image ? (
                            <Box component="img" src={row.image} alt="" sx={{ width: 44, height: 44, flexShrink: 0, objectFit: 'contain' }} />
                          ) : (
                            <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 1, background: '#F3EFFC' }} />
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontWeight: 700, fontSize: '15px', color: '#1D1F2C', fontFamily: FONT }} title={row.productNumber || row.partNumber}>
                              {row.productNumber || row.partNumber}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '13.5px', color: MUTED, fontFamily: FONT }} title={description}>
                              {description || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...cellTextSx, fontWeight: 600, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.35 }}>{categoryName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...cellTextSx, fontWeight: 600 }}>{row.stock || 0}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...cellTextSx, fontWeight: 600 }}>{firstBreak ? `${firstBreak.breakQty} : ₹${firstBreak.unitPrice}` : '_'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...cellTextSx, fontWeight: 600 }}>{Number(row.gst ?? 18)}%</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...cellTextSx, fontWeight: 600 }}>
                          {firstBreak ? `${firstBreak.breakQty} : ₹${finalPriceWithGst(firstBreak.unitPrice, row.gst ?? 18).toFixed(3)}` : '_'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={uploaded ? 'Uploaded' : STATUS_LABELS[row.status] || 'Published'}
                          sx={{
                            fontWeight: 600,
                            borderRadius: '6px',
                            fontFamily: FONT,
                            ...(STATUS_COLORS[uploaded ? 'UPLOADED' : row.status] || STATUS_COLORS.PUBLISHED),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...cellTextSx, color: MUTED }}>{formatAdded(row.fetchedAt || item.fetchedAt)}</Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', px: 0.5 }}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setViewItem(item)} sx={{ color: '#8791A5' }}><VisibilityOutlined fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: PRIMARY }}><EditOutlined fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteItem(item)} sx={{ color: '#8791A5' }}><DeleteOutline fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {status !== 'UPLOADED' && visibleItems.length > 0 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: '13px', color: MUTED }}>
              <b style={{ color: '#1D1F2C' }}>{selected.size}</b> of {selectable.length} selected
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="text" onClick={() => setSelected(new Set())} disabled={!selected.size} sx={{ textTransform: 'none', color: MUTED, fontWeight: 600 }}>
                Deselect all
              </Button>
              <Button variant="outlined" startIcon={<DeleteOutline />} onClick={() => setBulkDeleteConfirm(items.filter((item) => selected.has(item._id)))} disabled={!selected.size} sx={{ textTransform: 'none', color: '#D92D20', borderColor: '#F2B8B5', fontWeight: 600, '&:hover': { borderColor: '#D92D20', background: '#FFF5F4' } }}>
                Delete selected ({selected.size})
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={downloadSelected} disabled={!selected.size} sx={{ textTransform: 'none', color: PRIMARY, borderColor: PRIMARY, fontWeight: 600 }}>
                Download Excel ({selected.size})
              </Button>
              <Button variant="contained" startIcon={<CloudUpload />} onClick={uploadSelected} disabled={!selected.size} sx={{ textTransform: 'none', background: PRIMARY, fontWeight: 600 }}>
                Send selected to Bulk Upload ({selected.size})
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>

      {viewItem && (() => {
        const row = viewItem.row || {};
        const firstBreak = parsePriceBreaks(row.prices, row.moq)[0];
        const description = row.description || buildOwnDescription(row, categoryById);
        const categoryName = categoryById.get(row.categoryId)?.label || viewItem.categoryLabel || row.supplierCategory || '—';
        return (
          <Dialog open onClose={() => setViewItem(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Quick product details</DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {row.image ? <Box component="img" src={row.image} alt="" sx={{ width: 88, height: 88, objectFit: 'contain', border: `1px solid ${BORDER}`, borderRadius: '8px' }} /> : <Box sx={{ width: 88, height: 88, borderRadius: '8px', background: '#F3EFFC' }} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '18px', wordBreak: 'break-word' }}>{row.productNumber || row.partNumber}</Typography>
                  <Typography sx={{ color: MUTED, mt: 0.5 }}>{row.manufacturer || 'Manufacturer not available'}</Typography>
                </Box>
              </Stack>
              <Typography sx={{ fontSize: '14px', color: '#344054', lineHeight: 1.55, mb: 2 }}>{description || 'No description available.'}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>Category</Typography><Typography sx={{ fontWeight: 600 }}>{categoryName}</Typography></Box>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>Stock</Typography><Typography sx={{ fontWeight: 600 }}>{row.stock || 0}</Typography></Box>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>Unit price</Typography><Typography sx={{ fontWeight: 600 }}>{firstBreak ? `₹${firstBreak.unitPrice}` : '—'}</Typography></Box>
                <Box><Typography sx={{ fontSize: '12px', color: MUTED }}>GST</Typography><Typography sx={{ fontWeight: 600 }}>{Number(row.gst ?? 18)}%</Typography></Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setViewItem(null)} sx={{ textTransform: 'none', color: MUTED }}>Close</Button>
              <Button variant="contained" onClick={() => { setViewItem(null); openEdit(viewItem); }} sx={{ textTransform: 'none', background: PRIMARY }}>Edit product</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {editingItem && (
        <RowEditor
          row={editingItem.row}
          attributeOptions={attributeOptions}
          categoryOptions={categoryOptions}
          categoryById={categoryById}
          onClose={() => !actionLoading && setEditingItem(null)}
          onSave={(_rowId, nextRow) => saveEdit(nextRow)}
        />
      )}

      {deleteItem && (
        <Dialog open onClose={() => !actionLoading && setDeleteItem(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.25, pb: 1.25 }}>
            <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: '50%', background: '#FFF1F0', color: '#D92D20' }}>
              <DeleteOutline fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '20px', lineHeight: 1.25 }}>Delete quick product?</Typography>
              <Typography sx={{ color: MUTED, fontSize: '13px', fontWeight: 400, mt: 0.25 }}>This action cannot be undone.</Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ py: 2.25 }}>
            <Box sx={{ border: '1px solid #F1D5D2', borderRadius: '10px', background: '#FFFAFA', px: 1.5, py: 1.25 }}>
              <Typography sx={{ color: '#667085', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>Quick product</Typography>
              <Typography sx={{ color: '#1D1F2C', fontWeight: 700, wordBreak: 'break-word' }}>{deleteItem.row?.productNumber || deleteItem.row?.partNumber}</Typography>
              <Typography sx={{ color: MUTED, fontSize: '13px', mt: 0.35, lineHeight: 1.45 }}>{deleteItem.row?.manufacturer || 'Manufacturer not available'}</Typography>
            </Box>
            <Typography sx={{ color: MUTED, fontSize: '14px', mt: 1.75, lineHeight: 1.55 }}>The product will be permanently removed from All Quick Products.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, background: '#FAFAFC' }}>
            <Button onClick={() => setDeleteItem(null)} disabled={actionLoading} variant="outlined" sx={{ textTransform: 'none', color: '#475467', borderColor: '#D0D5DD', px: 2 }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDelete} disabled={actionLoading} sx={{ textTransform: 'none', px: 2.25, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>{actionLoading ? 'Deleting…' : 'Delete product'}</Button>
          </DialogActions>
        </Dialog>
      )}

      {bulkDeleteConfirm && (
        <Dialog open onClose={() => !actionLoading && setBulkDeleteConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Delete selected quick products?</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: MUTED, fontSize: '14px', lineHeight: 1.55 }}>
              {bulkDeleteConfirm.length} selected product(s) will be permanently removed from All Quick Products.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => setBulkDeleteConfirm(null)} disabled={actionLoading} sx={{ textTransform: 'none', color: MUTED }}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmBulkDelete} disabled={actionLoading} sx={{ textTransform: 'none', boxShadow: 'none' }}>
              {actionLoading ? 'Deleting…' : `Delete ${bulkDeleteConfirm.length} product(s)`}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <DeleteModal
        isOpen={clearAllConfirm}
        onClose={() => setClearAllConfirm(false)}
        onDelete={confirmClearAll}
        heading="all quick products"
        description="This permanently removes every Quick Product. Enter the Super Admin password to continue."
        loading={actionLoading}
      />

      {uploadConfirm && (() => {
        const { chosen, existing } = uploadConfirm;
        const existingIds = new Set(existing.map((item) => item._id));
        const fresh = chosen.filter((item) => !existingIds.has(item._id));
        return (
          <Dialog open onClose={() => setUploadConfirm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: '#FFFAEB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WarningAmber sx={{ color: '#DC6803' }} />
              </Box>
              Some products are already in your catalog
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: '14px', color: MUTED, fontFamily: FONT, mb: 1.5 }}>
                <b style={{ color: '#1D1F2C' }}>{existing.length}</b> of the {chosen.length} selected product(s) already exist. You can update them with
                the new details, or upload only the new ones and leave the existing products unchanged.
              </Typography>
              <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', maxHeight: 220, overflowY: 'auto' }}>
                {existing.map((item, index) => (
                  <Stack
                    key={item._id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 1.75, py: 1, borderTop: index ? `1px solid ${BORDER}` : 'none' }}
                  >
                    <Typography sx={{ fontSize: '13.5px', fontWeight: 600, fontFamily: FONT }}>{item.row?.productNumber || item.row?.partNumber}</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: MUTED, fontFamily: FONT }}>Already in catalog</Typography>
                  </Stack>
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={() => setUploadConfirm(null)} sx={{ textTransform: 'none', color: MUTED, fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={() => startUpload(fresh)}
                disabled={!fresh.length}
                sx={{ textTransform: 'none', color: PRIMARY, borderColor: PRIMARY, fontWeight: 600 }}
              >
                Upload only new ({fresh.length})
              </Button>
              <Button variant="contained" onClick={() => startUpload(chosen)} sx={{ textTransform: 'none', background: PRIMARY, fontWeight: 600, boxShadow: 'none' }}>
                Update existing & upload all ({chosen.length})
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </Box>
  );
};

export default AllQuickProducts;
